/**
 * The routes that exchange credentials for a token.
 *
 * A 401 from one of these means "those credentials are wrong", which the form
 * that made the request renders itself. A 401 from anywhere else means the
 * session has expired, which is what the global handler's redirect is for.
 * Telling the two apart is the whole point of this module: without it a wrong
 * password triggers a full page load that wipes the error message before it
 * can be read.
 *
 * Note `passkeys/options` is the sign-in challenge while `passkeys/register`
 * and `passkeys/register/options` are enrolment from Settings, which needs a
 * session. A prefix match on `/passkeys` would wrongly exempt those.
 */
const SIGN_IN_PATHS = [
  '/api/v1/auth/login',
  '/api/v1/auth/passkeys/options',
  '/api/v1/auth/passkeys/verify',
  '/api/v1/auth/sso/microsoft',
] as const;

/** `/social/google`, `/social/apple`, and any provider added later. */
const SIGN_IN_PREFIXES = ['/api/v1/auth/social/'] as const;

/**
 * Was this request an attempt to sign in, rather than an authenticated call?
 *
 * Fails safe: an unrecognised or missing url counts as an ordinary request, so
 * a genuine expiry is never silently swallowed.
 */
export function isSignInAttempt(url: string | undefined): boolean {
  if (!url) return false;

  // Requests are made with relative urls, but take the path out of an absolute
  // one too rather than depending on that, and drop any query string.
  const path = url.startsWith('http') ? safePathname(url) : url.split('?')[0];

  return SIGN_IN_PATHS.some((p) => p === path) || SIGN_IN_PREFIXES.some((p) => path.startsWith(p));
}

function safePathname(url: string): string {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
}
